import { snakeize } from 'casing'
import { API } from '../../../common/api'
import {
  azureEngineServiceUrl,
  crmServiceUrl,
  engineServiceUrl,
  labravAWSEngineServiceUrl,
} from '../../../common/utils/constants'
import { ActionType } from '../../../externalApps/modules/referrals/actions'

export type SaveReferralAPIParams = {
  referralObject: Record<string, unknown>
  invokeSync: boolean
  referralMetadata: {
    crmId: string
    crmParentTable: string
    crmPrimaryKeyName: string
    crmPrimaryKeyValue?: string
    referralType: string
    referralStatus: 'draft' | 'submitted'
    referralId?: string
  }
  labraSettingObject: {
    consultingPartner: boolean
    technologyPartner: boolean
    isv: boolean
    srrp: boolean
    accelerateProgram: boolean
    awsFieldsVersion: string
  }
}

export const saveReferral = (
  params: SaveReferralAPIParams,
  referralId?: string,
  actionType?: ActionType,
  templateId?: string
) => {
  const referralObject = params.referralObject
  const _params = snakeize(params)
  const endpoint = referralId
    ? `${crmServiceUrl}/referrals/${referralId}`
    : `${crmServiceUrl}/referrals`

  // Determine HTTP method based on actionType and referralId
  let method: 'post' | 'put' | 'patch'
  if (actionType === 'put') {
    method = 'put'
  } else if (actionType === 'patch') {
    method = 'patch'
  } else if (referralId) {
    method = 'put'
  } else {
    method = 'post'
  }

  return API(`${endpoint}`, {
    method,
    params: {
      template_id: templateId,
    },
    data: {
      ..._params,
      sync_direction: 'crm_to_ace',
      referral_object: referralObject,
    },
  })
}

export const saveReferralAzure = (
  referralPayload: Record<string, unknown>,
  partnerId: string,
  referralId?: string,
  crmType: string = 'hubspot'
) => {
  const endpoint = `${azureEngineServiceUrl}/partners/${partnerId}/referrals`
  return API(`${endpoint}`, {
    method: 'put',
    data: referralPayload,
  })
}

export const fetchReferralAzure = (
  partnerId: string,
  referralId: string,
  crmType: string = 'hubspot'
) => {
  const endpoint = `${azureEngineServiceUrl}/partners/${partnerId}/referrals/${referralId}`
  return API.get(endpoint, {
    params: {
      crm_type: crmType,
    },
  })
}

export type AzureTeamMember = {
  firstName: string
  lastName: string
  email: string
  phoneNumber?: string
}

export const fetchAzureTeamMembers = async (
  partnerId: string
): Promise<AzureTeamMember[]> => {
  const response = await API.get<AzureTeamMember[]>(
    `${azureEngineServiceUrl}/partners/${partnerId}/team-members`
  )
  return response.data
}

export type AzureReferralSolution = {
  id: string
  solution_id: string
  solution_name: string
  publisher_name: string
  solution_type:
    | 'COMPANY_SOLUTION'
    | 'MICROSOFT_SOLUTION'
    | 'OTHER_THIRD_PARTY_SOLUTION'
}

export const fetchAzureReferralSolutions = async (
  partnerId: string
): Promise<AzureReferralSolution[]> => {
  const response = await API.get<AzureReferralSolution[]>(
    `${azureEngineServiceUrl}/partners/${partnerId}/referral-solutions`
  )
  return response.data
}

export const fetchReferral = (referralId?: string) => {
  return API.get(`${crmServiceUrl}/referrals/${referralId}`)
}

export const deleteReferral = (referralId?: string) => {
  return API.delete(`${crmServiceUrl}/referrals/${referralId}`)
}
export const fetchReferralSyncRecords = (referralId?: string) => {
  return API.get(`${crmServiceUrl}/referrals/${referralId}/syncs`)
}

export const apiFetchLabraReferralStatusHistory = (
  partnerId: string,
  referralId: string
) => {
  return API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/referrals/${referralId}/status_histories`
  )
}

export const fetchPartnerIQOverview = (partnerId: string) =>
  API.get(`${engineServiceUrl}/partners/${partnerId}/partner-iq/overview`)

export const fetchAwsOpportunity = (
  partnerId: string,
  aceOpportunityId: string
) =>
  API.get(
    `${labravAWSEngineServiceUrl}/partners/${partnerId}/aws-opportunity/${aceOpportunityId}`
  )

export type DescriptionRewriteRequest = {
  description: string
  mode?: string
}

export type DescriptionRewriteResponse = {
  rewrittenDescription: string
  originalLength?: number
  rewrittenLength?: number
  withinLimit?: boolean
}

export const rewriteDescription = async (data: DescriptionRewriteRequest) => {
  const response = await API.post(
    `${labravAWSEngineServiceUrl}/referral/description/rewrite`,
    data
  )
  return {
    ...response,
    data: response.data as DescriptionRewriteResponse,
  }
}
